/**
 * Onboarding Modal
 * 
 * Progressive onboarding flow for new users with context-aware steps.
 * Issue #563 - First-Run Onboarding Flow
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import type { Me, OnboardingContext } from '@/lib/api/auth';
import { authApi } from '@/lib/api/auth';
import WelcomeStep from './steps/WelcomeStep';
import ProfileBasicsStep from './steps/ProfileBasicsStep';
import ActivitiesStep from './steps/ActivitiesStep';
import NotificationsStep from './steps/NotificationsStep';
import CompletionStep from './steps/CompletionStep';

interface OnboardingModalProps {
  user: Me;
  context: OnboardingContext;
}

type StepType = 'welcome' | 'profile' | 'activities' | 'notifications' | 'completion';

export default function OnboardingModal({ user, context }: OnboardingModalProps) {
  const navigate = useNavigate();
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Determine which steps to show based on context
  const steps: StepType[] = ['welcome', 'profile', 'activities', 'notifications', 'completion'];
  const currentStep = steps[currentStepIndex];

  // Prevent body scroll when modal is open
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  // Prevent ESC key from closing modal (non-dismissible)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  const handleNext = () => {
    if (currentStepIndex < steps.length - 1) {
      setCurrentStepIndex((prev) => prev + 1);
    }
  };

  const handleBack = () => {
    if (currentStepIndex > 0) {
      setCurrentStepIndex((prev) => prev - 1);
    }
  };

  const handleComplete = async () => {
    setIsSubmitting(true);
    try {
      await authApi.completeOnboarding();

      // Navigate based on context type
      switch (context.type) {
        case 'club':
          navigate(`/clubs/${context.club_id}`);
          break;
        case 'event':
          navigate(`/events/${context.event_id}`);
          break;
        case 'founder':
          navigate(context.club_id ? `/clubs/${context.club_id}` : '/clubs/create');
          break;
        case 'clean':
        default:
          navigate('/home');
          break;
      }
    } catch (error) {
      console.error('Failed to complete onboarding:', error);
      // Don't prevent navigation on error - fail gracefully
      navigate('/home');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4">
      <div 
        className="w-full max-w-md rounded-2xl bg-[--oro-surface-primary] shadow-2xl animate-oro-modal-enter overflow-hidden"
        role="dialog"
        aria-modal="true"
        aria-labelledby="onboarding-title"
      >
        {currentStep === 'welcome' && (
          <WelcomeStep
            user={user}
            context={context}
            onNext={handleNext}
          />
        )}

        {currentStep === 'profile' && (
          <ProfileBasicsStep
            user={user}
            onNext={handleNext}
            onBack={handleBack}
          />
        )}

        {currentStep === 'activities' && (
          <ActivitiesStep
            onNext={handleNext}
            onBack={handleBack}
          />
        )}

        {currentStep === 'notifications' && (
          <NotificationsStep
            onNext={handleNext}
            onBack={handleBack}
          />
        )}

        {currentStep === 'completion' && (
          <CompletionStep
            context={context}
            onComplete={handleComplete}
            onBack={handleBack}
            isSubmitting={isSubmitting}
          />
        )}
      </div>
    </div>
  );
}
